/* Storefront writes and private reads must go through Shopify's authenticated proxy. */
(() => {
  'use strict';
  const root = () => document.querySelector('[data-modl-proxy]');
  const base = () => {
    const path = root()?.dataset.modlProxy || '/apps/modl-community';
    if (!/^\/apps\/[a-z0-9_-]+$/.test(path)) throw new Error('Community connection is not configured.');
    return path;
  };
  async function fetchTimed(path, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try { return await fetch(base() + path, { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }
  async function session() {
    const response = await fetchTimed('/api/session');
    const data = await response.json().catch(() => ({}));
    const authRequired = response.status === 401 || (response.status === 403 && data?.code === 'AUTH_REQUIRED');
    const differentCustomer = response.ok && data?.customer_id && data.customer_id !== root()?.dataset.customerId;
    if (authRequired || differentCustomer) {
      window.dispatchEvent(new CustomEvent('modl-auth-lost'));
      throw new Error('Please sign in again to continue.');
    }
    // Proxy outages and unexpected HTML are connection failures, not a logout.
    // Reject the request without clearing feature state or sending an unverified write.
    if (!response.ok || !data?.customer_id || typeof data.csrf !== 'string' || !data.csrf) {
      throw new Error('The forum connection is temporarily unavailable. Please try again.');
    }
    return data;
  }
  async function request(path, options = {}, identity) {
    const target = path.split('?')[0].replace(/\/+$/, '').toLowerCase();
    const write = String(options.method || 'GET').toUpperCase() === 'POST';
    let likes = target === '/api/likes';
    if (likes) { try { likes = JSON.parse(options.body).liked !== false; } catch {} }
    const participation = write && (likes || ['/api/create-topic','/api/customer-profile','/api/customer-metafields','/api/dm/send','/api/edits'].includes(target));
    if (participation && window.ModlCommunityTerms && !await window.ModlCommunityTerms.ensure())
      throw new Error('Complete your forum profile setup to continue. Your changes have not been sent.');
    const verified = identity || await session();
    const response = await fetchTimed(path, {
      ...options,
      headers: { ...options.headers, ...(options.method && options.method !== 'GET' ? { 'X-Modl-CSRF': verified.csrf } : {}) },
    });
    if (response.status === 401 || (response.status === 403 && (await response.clone().json().catch(() => ({}))).code === 'AUTH_REQUIRED')) {
      window.dispatchEvent(new CustomEvent('modl-auth-lost'));
    }
    if (response.status === 403 && (await response.clone().json().catch(() => ({}))).code === 'TERMS_REQUIRED') window.ModlCommunityTerms?.invalidate();
    return response;
  }
  // Public likes reads still pass through Shopify's signed app proxy.
  function publicLikes(path) {
    if (!['', '/counts', '/people'].includes(path.split('?')[0]))
      throw new Error('Invalid public likes request.');
    return fetchTimed('/api/likes' + path);
  }
  function publicEditing(path) {
    if (!['/session', '/updates'].includes(path.split('?')[0])) throw new Error('Invalid editing read.');
    return fetchTimed('/api/edits' + path);
  }
  function linkedPages(path) {
    if (!['/options','/community-highlights'].includes(path) && !/^\/page\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(path) && !path.startsWith('/preview?url=')) throw new Error('Invalid linked page.');
    return fetchTimed('/api/linked-pages' + path);
  }
  window.ModlCommunityAPI = { session, request, publicLikes, publicEditing, linkedPages };
})();
